import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  PictureAsPdf,
  Refresh,
  Search,
  Visibility
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';
import { MRO_COMPONENT_TAGS, DEFAULT_MRO_COMPONENT_TAG } from '../../constants/mroComponentTags';
import MaintenanceWorkflowStatusCard from '../maintenance/MaintenanceWorkflowStatusCard';
import WorkOrderIssuedPartsPanel from '../maintenance/components/WorkOrderIssuedPartsPanel';
import AMPChecksTab from '../maintenance/components/AMPChecksTab';
import WorkOrderSecondPageDialog from '../maintenance/WorkOrderSecondPageDialog';
import EngineeringDefectReportDialog from '../maintenance/EngineeringDefectReportDialog';
import { mroReferenceNumber, camoWorkOrderNumber } from './mroLabLanes';

const formatUserName = (user) => {
  if (!user) return '—';
  if (typeof user === 'string') return user;
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const DetailField = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{children ?? '—'}</Typography>
  </Grid>
);

const SummaryTable = ({ title, headers, rows, emptyMessage }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      {title} {rows.length > 0 ? `(${rows.length})` : ''}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header} sx={{ fontWeight: 700 }}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

const Section = ({ title, children, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      borderLeft: accent ? `4px solid ${accent}` : undefined
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

function MroLabWorkOrderDetail({ workOrder: initialWo }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [wo, setWo] = useState(initialWo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionTakenOpen, setActionTakenOpen] = useState(false);
  const [edrOpen, setEdrOpen] = useState(false);
  const [busyDoc, setBusyDoc] = useState('');
  const [tagPartNumber, setTagPartNumber] = useState('');
  const [tagType, setTagType] = useState(DEFAULT_MRO_COMPONENT_TAG);
  const [tagReason, setTagReason] = useState('');
  const [tagRemarks, setTagRemarks] = useState('');
  const [foundComponent, setFoundComponent] = useState(null);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState('');

  const loadFull = useCallback(async () => {
    if (!initialWo?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/maintenance/${initialWo._id}`);
      setWo(res.data || initialWo);
    } catch (err) {
      console.error('Lab WO detail load failed', err);
      setWo(initialWo);
      setError(err.response?.data?.message || 'Could not refresh full work order; showing queue summary.');
    } finally {
      setLoading(false);
    }
  }, [initialWo]);

  useEffect(() => {
    setWo(initialWo);
    setFoundComponent(null);
    setTagPartNumber('');
    setTagError('');
    loadFull();
  }, [initialWo, loadFull]);

  const downloadCrs = async () => {
    if (!wo?._id || !wo?.crsDocument?.originalName) return;
    setBusyDoc('crs');
    try {
      await downloadAuthenticatedPdf(
        `/api/maintenance/${wo._id}/crs/download`,
        wo.crsDocument.originalName || `crs-${wo._id}`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download CRS', 'error');
    } finally {
      setBusyDoc('');
    }
  };

  const downloadCame = async () => {
    if (!wo?._id) return;
    setBusyDoc('came');
    try {
      const filename = `CAME-FORM-04_${String(wo.workOrderNumber || wo._id).replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`;
      await downloadAuthenticatedPdf(
        `/api/maintenance/${wo._id}/came-work-order-form.pdf?download=1`,
        filename
      );
    } catch (err) {
      showToast(err.message || 'Failed to download CAME/FORM/04', 'error');
    } finally {
      setBusyDoc('');
    }
  };

  const downloadActionTakenPdf = async () => {
    if (!wo?._id) return;
    setBusyDoc('atf');
    try {
      await downloadAuthenticatedPdf(
        `/api/maintenance/${wo._id}/action-taken-form.pdf?download=1`,
        `Action-Taken-Form_${String(wo.workOrderNumber || wo._id).replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download Action Taken Form PDF', 'error');
    } finally {
      setBusyDoc('');
    }
  };

  const downloadCsv = async () => {
    if (!wo?._id || !wo?.workOrderCsvDocument?.originalName) return;
    setBusyDoc('csv');
    try {
      await downloadAuthenticatedPdf(
        `/api/maintenance/${wo._id}/work-order-csv/download`,
        wo.workOrderCsvDocument.originalName || `work-order-${wo._id}.csv`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download CSV', 'error');
    } finally {
      setBusyDoc('');
    }
  };

  const findComponent = async () => {
    const aircraftId = wo?.aircraftId?._id || wo?.aircraftId;
    const pn = String(tagPartNumber || '').trim();
    if (!aircraftId || !pn) {
      setTagError('Aircraft and part number are required');
      return;
    }
    setTagBusy(true);
    setTagError('');
    setFoundComponent(null);
    try {
      const res = await axios.get('/api/components', {
        params: { aircraftId, isActive: true, pageSize: 500 }
      });
      const list = Array.isArray(res.data?.items)
        ? res.data.items
        : (Array.isArray(res.data) ? res.data : []);
      const match = list.find((c) =>
        String(c.partNumber || c.partId?.partNumber || '').trim().toLowerCase() === pn.toLowerCase()
      );
      if (!match) {
        setTagError('No active component found for that part number on this aircraft');
        return;
      }
      setFoundComponent(match);
    } catch (err) {
      setTagError(err.response?.data?.message || 'Component lookup failed');
    } finally {
      setTagBusy(false);
    }
  };

  const downloadTagOrCert = async () => {
    if (!foundComponent?._id || !wo?._id) {
      setTagError('Find a component first');
      return;
    }
    const tagMeta = MRO_COMPONENT_TAGS.find((t) => t.key === tagType) || MRO_COMPONENT_TAGS[0];
    const opId = wo.operatorId?._id || wo.operatorId;
    const safePart = String(foundComponent.partNumber || foundComponent.partId?.partNumber || 'part')
      .replace(/[^a-zA-Z0-9._-]+/g, '_');
    const safeSerial = String(foundComponent.serialNumber || '').trim().replace(/[^a-zA-Z0-9._-]+/g, '_');
    setTagBusy(true);
    setTagError('');
    try {
      if (tagMeta.isCertificate || tagType === 'REMOVAL_CERTIFICATE') {
        const qs = new URLSearchParams({
          download: '1',
          actionType: 'REMOVE',
          workOrderId: String(wo._id)
        });
        if (opId) qs.set('operatorId', String(opId));
        if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
        const filename = safeSerial
          ? `Component-Removal-Certificate_${safePart}_${safeSerial}.pdf`
          : `Component-Removal-Certificate_${safePart}.pdf`;
        await downloadAuthenticatedPdf(
          `/api/components/${foundComponent._id}/removal-certificate.pdf?${qs.toString()}`,
          filename
        );
        showToast('Removal certificate downloaded', 'success');
      } else {
        const qs = new URLSearchParams({
          download: '1',
          tagType,
          workOrderId: String(wo._id)
        });
        if (opId) qs.set('operatorId', String(opId));
        if (wo.operatorCode) qs.set('operatorCode', String(wo.operatorCode).trim());
        if (tagReason) qs.set('reason', tagReason.trim());
        if (tagRemarks) qs.set('remarks', tagRemarks.trim());
        const formNo = String(tagMeta.formNo || 'TAG').replace(/[^a-zA-Z0-9._-]+/g, '_');
        const filename = safeSerial
          ? `${formNo}_${safePart}_${safeSerial}.pdf`
          : `${formNo}_${safePart}.pdf`;
        await downloadAuthenticatedPdf(
          `/api/components/${foundComponent._id}/mro-tag.pdf?${qs.toString()}`,
          filename
        );
        showToast(`${tagMeta.label} downloaded`, 'success');
      }
    } catch (err) {
      const msg = err.message || err.response?.data?.message || 'Failed to generate tag/certificate PDF';
      setTagError(msg);
      showToast(msg, 'error');
    } finally {
      setTagBusy(false);
    }
  };

  if (!wo) return null;

  const spareRows = (wo.requiredSpares || []).map((item) => [
    item.partNumber || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.taskReference || '—'
  ]);
  const consumableRows = (wo.requiredConsumables || []).map((item) => [
    item.partNumberOrSpecification || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.expiry ? dayjs(item.expiry).format('YYYY-MM-DD') : '—'
  ]);
  const toolRows = (wo.requiredToolsEquipment || []).map((item) => [
    item.partNumber || '—',
    item.itemName || '—',
    item.due ? dayjs(item.due).format('YYYY-MM-DD') : '—',
    item.taskReferenceId || '—'
  ]);
  const hasActionTaken = Boolean(wo.workOrderSecondPage?.savedAt);

  return (
    <Stack spacing={2.5}>
      <Alert severity="info" variant="outlined">
        Read-only lab view. Forms, certificates, and tags can be opened or downloaded; accept / complete / QM decisions stay on the operational Workbench.
      </Alert>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {mroReferenceNumber(wo)} · {wo.title || 'Untitled'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            CAMO WO {camoWorkOrderNumber(wo)} · {wo.aircraftId?.registration || 'No aircraft'}
          </Typography>
        </Box>
        <Button size="small" startIcon={<Refresh />} onClick={loadFull} disabled={loading}>
          Refresh detail
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}
      {error ? <Alert severity="warning">{error}</Alert> : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={wo.status || '—'} />
        <Chip size="small" variant="outlined" label={`Priority: ${wo.priority || '—'}`} />
        <Chip size="small" variant="outlined" label={`Planning: ${wo.planningApproved ? 'Approved' : 'Pending'}`} />
        <Chip size="small" variant="outlined" label={`MRO accepted: ${wo.mroAccepted ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`Engineer done: ${wo.mroEngineerCompleted ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`MRO QM ack: ${wo.mroQmCompletionAcknowledged ? 'Yes' : 'No'}`} />
        <Chip size="small" variant="outlined" label={`CAMO QM ack: ${wo.camoQmCompletionAcknowledged ? 'Yes' : 'No'}`} />
      </Stack>

      <Section title="Forms & certificates" accent="#1565c0">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            disabled={!wo.crsDocument?.originalName || busyDoc === 'crs'}
            onClick={downloadCrs}
          >
            {wo.crsDocument?.originalName ? `Download CRS (${wo.crsDocument.originalName})` : 'CRS not uploaded'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdf />}
            disabled={busyDoc === 'came'}
            onClick={downloadCame}
          >
            {busyDoc === 'came' ? 'Downloading…' : 'Download CAME/FORM/04'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => setActionTakenOpen(true)}
          >
            Action Taken Form
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdf />}
            disabled={!hasActionTaken || busyDoc === 'atf'}
            onClick={downloadActionTakenPdf}
          >
            {hasActionTaken ? 'Action Taken PDF' : 'Action Taken PDF (not saved)'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => setEdrOpen(true)}
          >
            Engineering Defect Report (EDR)
          </Button>
          {wo.workOrderCsvDocument?.originalName ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              disabled={busyDoc === 'csv'}
              onClick={downloadCsv}
            >
              Download Maintenance CSV
            </Button>
          ) : null}
        </Stack>
        {hasActionTaken ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Action Taken saved {formatDate(wo.workOrderSecondPage.savedAt)}
            {wo.workOrderSecondPage.savedBy ? ` by ${formatUserName(wo.workOrderSecondPage.savedBy)}` : ''}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            No Action Taken Form saved yet — open the form to inspect the template (save is blocked for Spectator).
          </Typography>
        )}
      </Section>

      <Section title="Component tags & removal certificate" accent="#6a1b9a">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Same GETS-MRO tags as the Workbench. Find a component on this aircraft, then generate a tag or removal certificate PDF.
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label="Part number"
            value={tagPartNumber}
            onChange={(e) => setTagPartNumber(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<Search />}
            onClick={findComponent}
            disabled={tagBusy}
          >
            Find component
          </Button>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Document</InputLabel>
            <Select
              label="Document"
              value={tagType}
              onChange={(e) => setTagType(e.target.value)}
            >
              {MRO_COMPONENT_TAGS.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.label} ({t.formNo})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            startIcon={<PictureAsPdf />}
            onClick={downloadTagOrCert}
            disabled={tagBusy || !foundComponent}
          >
            Generate PDF
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 1 }}>
          <TextField
            size="small"
            label="Tag reason (optional)"
            value={tagReason}
            onChange={(e) => setTagReason(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Tag remarks (optional)"
            value={tagRemarks}
            onChange={(e) => setTagRemarks(e.target.value)}
            fullWidth
          />
        </Stack>
        {foundComponent ? (
          <Alert severity="success" sx={{ mb: 1 }}>
            Found {foundComponent.partNumber || foundComponent.partId?.partNumber || 'component'}
            {foundComponent.serialNumber ? ` · S/N ${foundComponent.serialNumber}` : ''}
            {foundComponent.code ? ` · ${foundComponent.code}` : ''}
          </Alert>
        ) : null}
        {tagError ? <Alert severity="error">{tagError}</Alert> : null}
      </Section>

      <Section title="Issued parts" accent="#2e7d32">
        <WorkOrderIssuedPartsPanel workOrderId={wo._id} />
      </Section>

      <Section title="AMP / engineer checklist" accent="#e65100">
        <AMPChecksTab workOrder={wo} user={user} title="Checklist" />
      </Section>

      <Section title="Material & tool requirements">
        <SummaryTable
          title="Required spares"
          headers={['Part #', 'Qty', 'Name', 'Task ref']}
          rows={spareRows}
          emptyMessage="No required spares recorded."
        />
        <SummaryTable
          title="Required consumables"
          headers={['Spec / PN', 'Qty', 'Name', 'Expiry']}
          rows={consumableRows}
          emptyMessage="No required consumables recorded."
        />
        <SummaryTable
          title="Required tools / equipment"
          headers={['Part #', 'Name', 'Due', 'Task ref']}
          rows={toolRows}
          emptyMessage="No required tools recorded."
        />
      </Section>

      <Section title="Assignment & completion">
        <Grid container spacing={2}>
          <DetailField label="Current shift engineer">
            {formatUserName(wo.mroCurrentEngineerId || wo.mroAssignedEngineerId)}
          </DetailField>
          <DetailField label="Engineer team">
            {Array.isArray(wo.mroAssignedEngineerIds) && wo.mroAssignedEngineerIds.length
              ? wo.mroAssignedEngineerIds.map((e) => formatUserName(e)).filter(Boolean).join(', ') || '—'
              : formatUserName(wo.mroAssignedEngineerId)}
          </DetailField>
          <DetailField label="MRO accepted by">{formatUserName(wo.mroAcceptedBy)}</DetailField>
          <DetailField label="Engineer completed">{wo.mroEngineerCompleted ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Engineer completed by">{formatUserName(wo.mroEngineerCompletedBy)}</DetailField>
          <DetailField label="Engineer completed at">{formatDate(wo.mroEngineerCompletedAt)}</DetailField>
          <DetailField label="MRO QM acknowledged">{wo.mroQmCompletionAcknowledged ? 'Yes' : 'No'}</DetailField>
          <DetailField label="MRO QM by">{formatUserName(wo.mroQmCompletionAcknowledgedBy)}</DetailField>
          <DetailField label="MRO QM at">{formatDate(wo.mroQmCompletionAcknowledgedAt)}</DetailField>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Engineer notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {wo.mroEngineerNotes || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">MRO notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {wo.mroNotes || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Planning notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {wo.planningNotes || '—'}
            </Typography>
          </Grid>
        </Grid>
      </Section>

      <Divider />

      <Section title="Workflow status">
        <MaintenanceWorkflowStatusCard workOrder={wo} user={user} />
      </Section>

      <WorkOrderSecondPageDialog
        open={actionTakenOpen}
        onClose={() => setActionTakenOpen(false)}
        workOrder={wo}
        user={user}
      />
      <EngineeringDefectReportDialog
        open={edrOpen}
        onClose={() => setEdrOpen(false)}
        workOrder={wo}
        user={user}
      />
    </Stack>
  );
}

export default MroLabWorkOrderDetail;
